// ============================================================
// GBCMC · Evaluación Institucional — Familias 2026 (Semestre I)
// FUENTE DE VERDAD del contenido. Portado fielmente desde el
// prototipo (design-reference/survey/data.js). El texto, las
// opciones y el orden NO se modifican sin aprobación: provienen
// de un instrumento psicométrico validado.
// La etiqueta técnica (tag) se conserva para análisis; nunca se muestra.
// ============================================================
const CAMBIO = [
    'Mucho mejor que antes',
    'Un poco mejor que antes',
    'Igual que antes',
    'Un poco peor que antes',
    'Mucho peor que antes',
    'Soy nuevo en el colegio',
];
const CAMBIO_SIN_NUEVO = [
    'Mucho mejor que antes',
    'Un poco mejor que antes',
    'Igual que antes',
    'Un poco peor que antes',
    'Mucho peor que antes',
    'No he tenido cómo darme cuenta',
];
// Letras de curso disponibles por nivel. q4 (curso) las muestra según el nivel
// elegido en q3. Confirmadas por la Coordinación (jun-2026): las letras son
// contiguas desde la A y cada nivel tiene un número distinto de cursos. (Esto
// corrige el listado previo que traía una "R": la letra correcta es "E").
const COURSE_LETTERS = 'ABCDEFGHIJKLMN'.split('');
/** Primeras `n` letras del alfabeto de cursos (A, B, C…). */
function courses(n) {
    return COURSE_LETTERS.slice(0, n);
}
const COURSES_BY_LEVEL = {
    // Preescolar
    'Escuela inicial': courses(3), // A–C
    'Prekínder': courses(3), // A–C
    'Kínder': courses(4), // A–D
    'Transición': courses(7), // A–G
    'Primero': courses(13), // A–M
    // Primaria
    'Segundo': courses(13), // A–M
    'Tercero': courses(12), // A–L
    'Cuarto': courses(13), // A–M
    'Quinto': courses(10), // A–J
    // Bachillerato
    'Sexto': courses(9), // A–I
    'Séptimo': courses(6), // A–F
    'Octavo': courses(4), // A–D
    'Noveno': courses(3), // A–C
    'Décimo': courses(2), // A–B
    'Undécimo': courses(2), // A–B
};
/** Letras de curso para el nivel dado (fallback a la lista más larga). */
export function coursesForLevel(level) {
    return (typeof level === 'string' && COURSES_BY_LEVEL[level]) || courses(13);
}
export const CHAPTERS = [
    {
        id: 'datos',
        num: 1,
        label: 'Tu familia',
        title: 'Cuéntanos de tu familia',
        intro: 'Unas pocas preguntas para saber quién responde y poder leer mejor las respuestas de toda la comunidad.',
        illustration: 'family',
    },
    {
        id: 'directiva',
        num: 2,
        label: 'El rumbo del colegio',
        title: 'El colegio que construimos juntos',
        intro: 'Hablemos de los valores, la comunicación y las nuevas ideas que mueven al GBCMC.',
        illustration: 'flag',
    },
    {
        id: 'academica',
        num: 3,
        label: 'Cómo aprende tu hijo(a)',
        title: 'Cómo aprende tu hijo(a)',
        intro: 'Emociones, investigación, inglés, evaluación y docentes: el corazón de la experiencia escolar.',
        illustration: 'books',
    },
    {
        id: 'servicios',
        num: 4,
        label: 'Espacios y servicios',
        title: 'Los espacios y servicios de cada día',
        intro: 'Los espacios físicos del colegio, las áreas que te atienden, la ruta y el restaurante.',
        illustration: 'bus',
    },
    {
        id: 'comunidad',
        num: 5,
        label: 'Convivencia e inclusión',
        title: 'Convivir, acoger e incluir',
        intro: 'El clima escolar, la llegada de las familias nuevas y los apoyos para las habilidades diversas.',
        illustration: 'community',
    },
    {
        id: 'cierre',
        num: 6,
        label: 'Tu mirada final',
        title: 'Tu mirada final',
        intro: 'Para terminar: tu satisfacción general y lo que el colegio debería celebrar y mejorar.',
        illustration: 'sun',
    },
];
export const QUESTIONS = [
    // ───────────── I. DATOS GENERALES ─────────────
    {
        id: 'q1',
        chapter: 'datos',
        type: 'single',
        tag: 'Datos',
        text: '¿Quién responde este cuestionario?',
        options: ['Estudiante', 'Madre', 'Padre', 'Acudiente', 'Padres en conjunto'],
    },
    {
        id: 'q2',
        chapter: 'datos',
        type: 'single',
        tag: 'Datos',
        text: '¿En qué sección está su hijo(a)?',
        options: ['Preescolar', 'Primaria', 'Bachillerato'],
    },
    {
        id: 'q3',
        chapter: 'datos',
        type: 'single',
        tag: 'Datos',
        text: '¿En qué nivel se encuentra su hijo(a)?',
        optionsFn: (a) => {
            if (a.q2 === 'Preescolar')
                return ['Escuela inicial', 'Prekínder', 'Kínder', 'Transición', 'Primero'];
            if (a.q2 === 'Primaria')
                return ['Segundo', 'Tercero', 'Cuarto', 'Quinto'];
            return ['Sexto', 'Séptimo', 'Octavo', 'Noveno', 'Décimo', 'Undécimo'];
        },
    },
    {
        id: 'q4',
        chapter: 'datos',
        type: 'single',
        tag: 'Datos',
        compact: true,
        text: '¿En qué curso?',
        optionsFn: (a) => coursesForLevel(a.q3),
    },
    {
        id: 'q5',
        chapter: 'datos',
        type: 'single',
        tag: 'Datos',
        text: '¿Cuántos años lleva su familia vinculada al GBCMC?',
        options: ['Menos de 1 año', '1 a 3 años', '4 a 6 años', 'Más de 6 años'],
    },
    {
        id: 'q6',
        chapter: 'datos',
        type: 'single',
        tag: 'Datos',
        text: '¿Su hijo(a) ingresó al colegio este año (2026)?',
        options: ['Sí, es estudiante nuevo', 'No, viene de años anteriores'],
    },
    // ───────────── II. GESTIÓN DIRECTIVA ─────────────
    {
        id: 'q7',
        chapter: 'directiva',
        type: 'single',
        tag: 'Evidencia · Identidad',
        text: 'En este semestre, ¿cuántas veces ha visto en el colegio situaciones que reflejen los valores que se enseñan (respeto, inclusión, ayuda a los demás, creatividad…)?',
        options: ['Más de 3 veces', 'Entre 1 y 3 veces', 'Ninguna que recuerde', 'No he tenido cómo darme cuenta'],
    },
    {
        id: 'q8',
        chapter: 'directiva',
        type: 'likert',
        tag: 'Percepción de cambio · Identidad',
        text: '¿Cómo evalúa su sentido de pertenencia al GBCMC?',
        anchors: ['Muy bajo', 'Muy alto'],
    },
    {
        id: 'q8m',
        chapter: 'directiva',
        type: 'open',
        tag: 'Abierta condicional · Identidad',
        condition: (a) => typeof a.q8 === 'number' && a.q8 <= 6,
        text: 'Cuéntanos qué afecta tu sentido de pertenencia al colegio.',
        placeholder: 'Cuéntanos un ejemplo concreto…',
    },
    {
        id: 'q9',
        chapter: 'directiva',
        type: 'likert',
        tag: 'Satisfacción · Identidad',
        text: '¿Qué tan satisfecho(a) está con la coherencia entre los valores que el colegio enseña y lo que vive en el día a día?',
        anchors: ['Nada satisfecho(a)', 'Totalmente satisfecho(a)'],
    },
    {
        id: 'q9m',
        chapter: 'directiva',
        type: 'open',
        tag: 'Abierta condicional · Identidad',
        condition: (a) => typeof a.q9 === 'number' && a.q9 <= 6,
        text: 'Indique en qué situaciones los valores que el colegio enseña no se reflejan en el día a día.',
        placeholder: 'Cuéntanos un ejemplo concreto…',
    },
    {
        id: 'q10',
        chapter: 'directiva',
        type: 'single',
        tag: 'Evidencia · Comunicación',
        context: 'Piensa en las últimas 2 semanas.',
        text: '¿Cuántas veces le costó encontrar la información que necesitaba del colegio (horarios, eventos, requisitos, entre otros)?',
        options: ['Ninguna', '1 vez', 'Entre 2 y 3 veces', 'Más de 3 veces', 'No he necesitado buscar información recientemente'],
    },
    {
        id: 'q11',
        chapter: 'directiva',
        type: 'single',
        tag: 'Percepción de cambio · Comunicación',
        context: 'Comparado con el año pasado…',
        text: '¿Cuál es su sensación ante la atención que el colegio presta a sus inquietudes, quejas o sugerencias?',
        options: CAMBIO,
    },
    {
        id: 'q12',
        chapter: 'directiva',
        type: 'likert',
        tag: 'Satisfacción · Comunicación',
        text: '¿Qué tan satisfecho(a) está con la claridad y la oportunidad de las comunicaciones que recibe del colegio?',
        anchors: ['Nada satisfecho(a)', 'Totalmente satisfecho(a)'],
    },
    {
        id: 'q12m',
        chapter: 'directiva',
        type: 'open',
        tag: 'Abierta condicional · Comunicación',
        condition: (a) => typeof a.q12 === 'number' && a.q12 <= 6,
        text: 'Indique qué comunicaciones del colegio le resultan poco claras o le llegan tarde.',
        placeholder: 'Cuéntanos un ejemplo concreto…',
    },
    {
        id: 'q13',
        chapter: 'directiva',
        type: 'single',
        tag: 'Evidencia · Innovación',
        text: 'En este semestre, ¿su hijo(a) ha mencionado o ha llevado a casa algo que aprendió sobre cuidado del medio ambiente o sostenibilidad?',
        options: ['Sí, varias veces', 'Sí, una vez', 'No recuerdo', 'Mi hijo(a) no suele contar este tipo de cosas'],
    },
    {
        id: 'q14',
        chapter: 'directiva',
        type: 'single',
        tag: 'Percepción de cambio · Innovación',
        context: 'Comparado con el año pasado…',
        text: '¿Cómo describiría las nuevas y creativas formas de enseñar del colegio (proyectos socioformativos, ferias, clubes, misiones de aprendizaje, retos, uso del computador en clase, entre otras)?',
        options: CAMBIO,
    },
    {
        id: 'q15',
        chapter: 'directiva',
        type: 'likert',
        tag: 'Satisfacción · Innovación',
        text: '¿Qué tan satisfecho(a) está con la innovación pedagógica que promueve el GBCMC?',
        anchors: ['Nada satisfecho(a)', 'Totalmente satisfecho(a)'],
    },
    {
        id: 'q15m',
        chapter: 'directiva',
        type: 'open',
        tag: 'Abierta condicional · Innovación',
        condition: (a) => typeof a.q15 === 'number' && a.q15 <= 6,
        text: 'Indique qué aspectos de la innovación pedagógica deberían mejorar.',
        placeholder: 'Cuéntanos un ejemplo concreto…',
    },
    // ───────────── III. GESTIÓN ACADÉMICA ─────────────
    {
        id: 'q16',
        chapter: 'academica',
        type: 'single',
        tag: 'Percepción de cambio · Socioemocional',
        context: 'Comparado con el comienzo del año…',
        text: '¿Cómo describiría hoy la capacidad de su hijo(a) para manejar lo que siente (rabia, tristeza, frustración, ansiedad, entre otras)?',
        options: CAMBIO_SIN_NUEVO,
    },
    {
        id: 'q17',
        chapter: 'academica',
        type: 'likert',
        tag: 'Satisfacción · Socioemocional',
        text: '¿Qué tan satisfecho(a) está con el acompañamiento socioemocional que el colegio brinda a su hijo(a)?',
        anchors: ['Nada satisfecho(a)', 'Totalmente satisfecho(a)'],
    },
    {
        id: 'q17m',
        chapter: 'academica',
        type: 'open',
        tag: 'Abierta condicional · Socioemocional',
        condition: (a) => typeof a.q17 === 'number' && a.q17 <= 6,
        text: 'Indique en qué podría mejorar el acompañamiento socioemocional a su hijo(a).',
        placeholder: 'Cuéntanos un ejemplo concreto…',
    },
    {
        id: 'q18',
        chapter: 'academica',
        type: 'single',
        tag: 'Evidencia · Investigativas',
        text: 'En este semestre, ¿su hijo(a) ha trabajado en proyectos en los que investiga, formula preguntas o resuelve problemas reales (misiones, ferias, proyectos del salón…)?',
        options: ['Sí, varias veces', 'Sí, al menos una vez', 'No que yo sepa', 'No estoy seguro(a)'],
    },
    {
        id: 'q19',
        chapter: 'academica',
        type: 'single',
        tag: 'Percepción de cambio · Investigativas',
        context: 'Comparado con el año pasado…',
        text: '¿Cómo describiría la motivación de su hijo(a) para preguntar, investigar y averiguar por su cuenta?',
        options: CAMBIO,
    },
    {
        id: 'q20',
        chapter: 'academica',
        type: 'likert',
        tag: 'Satisfacción · Investigativas',
        text: '¿Qué tan satisfecho(a) está con el desarrollo de las habilidades de investigación y de pensamiento crítico de su hijo(a)?',
        anchors: ['Nada satisfecho(a)', 'Totalmente satisfecho(a)'],
    },
    {
        id: 'q20m',
        chapter: 'academica',
        type: 'open',
        tag: 'Abierta condicional · Investigativas',
        condition: (a) => typeof a.q20 === 'number' && a.q20 <= 6,
        text: 'Indique qué aspectos se pueden fortalecer para el desarrollo de la investigación y el pensamiento crítico de su hijo(a).',
        placeholder: 'Cuéntanos un ejemplo concreto…',
    },
    {
        id: 'q21',
        chapter: 'academica',
        type: 'single',
        tag: 'Evidencia · Bilingüismo',
        context: 'Piensa en el último mes.',
        text: '¿Su hijo(a) ha usado el inglés por su cuenta fuera del salón (ver series, leer, hablar, cantar, jugar, entre otras)?',
        options: ['Sí, varias veces', 'Sí, una o dos veces', 'No que yo sepa', 'No estoy seguro(a)'],
    },
    {
        id: 'q22',
        chapter: 'academica',
        type: 'single',
        tag: 'Percepción de cambio · Bilingüismo',
        context: 'Comparado con el año pasado…',
        text: '¿Cómo describiría hoy el nivel de inglés de su hijo(a)?',
        options: CAMBIO,
    },
    {
        id: 'q23',
        chapter: 'academica',
        type: 'likert',
        tag: 'Satisfacción · Bilingüismo',
        text: '¿Qué tan satisfecho(a) está con el programa de bilingüismo del GBCMC?',
        anchors: ['Nada satisfecho(a)', 'Totalmente satisfecho(a)'],
    },
    {
        id: 'q23m',
        chapter: 'academica',
        type: 'open',
        tag: 'Abierta condicional · Bilingüismo',
        condition: (a) => typeof a.q23 === 'number' && a.q23 <= 6,
        text: 'Indique qué aspectos del programa de bilingüismo deberían mejorar.',
        placeholder: 'Cuéntanos un ejemplo concreto…',
    },
    {
        id: 'q24',
        chapter: 'academica',
        type: 'single',
        tag: 'Evidencia · Evaluación por misiones',
        context: 'Piensa en el último periodo.',
        text: '¿Recibió del colegio información clara sobre lo que su hijo(a) aprendió y en qué necesita mejorar (no solo la nota)?',
        options: ['Sí, por escrito y completa', 'Sí, de palabra o breve', 'No la recibí', 'No estoy seguro(a)'],
    },
    {
        id: 'q25',
        chapter: 'academica',
        type: 'likert',
        tag: 'Satisfacción · Evaluación por misiones',
        text: '¿Qué tan satisfecho(a) está con el sistema de evaluación y con la información que recibe sobre el progreso de su hijo(a)?',
        anchors: ['Nada satisfecho(a)', 'Totalmente satisfecho(a)'],
    },
    {
        id: 'q25m',
        chapter: 'academica',
        type: 'open',
        tag: 'Abierta condicional · Evaluación por misiones',
        condition: (a) => typeof a.q25 === 'number' && a.q25 <= 6,
        text: 'Indique qué información o aspecto del sistema de evaluación debería mejorar.',
        placeholder: 'Cuéntanos un ejemplo concreto…',
    },
    {
        id: 'q26',
        chapter: 'academica',
        type: 'single',
        tag: 'Evidencia · Dirección de grupo',
        text: 'Este semestre, ¿el director(a) de grupo se ha comunicado con usted por iniciativa propia, no solo cuando ha habido un problema?',
        options: ['Sí, varias veces', 'Sí, al menos una vez', 'Solo cuando hubo problemas', 'No se ha comunicado'],
    },
    {
        id: 'q27',
        chapter: 'academica',
        type: 'single',
        tag: 'Evidencia · Docentes de área',
        text: 'Este semestre, ¿qué tan accesibles han sido los docentes de las distintas asignaturas para responder dudas o atender solicitudes sobre el aprendizaje de su hijo(a)?',
        options: ['Muy accesibles', 'Accesibles', 'Poco accesibles', 'Nada accesibles', 'No he tenido necesidad de contactarlos'],
    },
    {
        id: 'q28',
        chapter: 'academica',
        type: 'likert',
        tag: 'Satisfacción · Dirección de grupo',
        text: '¿Qué tan satisfecho(a) está con el director(a) de grupo de su hijo(a)?',
        anchors: ['Nada satisfecho(a)', 'Totalmente satisfecho(a)'],
    },
    {
        id: 'q28m',
        chapter: 'academica',
        type: 'open',
        tag: 'Abierta condicional · Dirección de grupo',
        condition: (a) => typeof a.q28 === 'number' && a.q28 <= 6,
        text: 'Indique qué aspectos del acompañamiento del director(a) de grupo deberían mejorar.',
        placeholder: 'Cuéntanos un ejemplo concreto…',
    },
    {
        id: 'q29',
        chapter: 'academica',
        type: 'likert',
        tag: 'Satisfacción · Equipo docente',
        text: '¿Qué tan satisfecho(a) está con el equipo docente de su hijo(a) en conjunto?',
        anchors: ['Nada satisfecho(a)', 'Totalmente satisfecho(a)'],
    },
    {
        id: 'q29m',
        chapter: 'academica',
        type: 'open',
        tag: 'Abierta condicional · Equipo docente',
        condition: (a) => typeof a.q29 === 'number' && a.q29 <= 6,
        text: 'Indique qué aspectos del equipo docente deberían mejorar.',
        placeholder: 'Cuéntanos un ejemplo concreto…',
    },
    // ───────────── IV. ESPACIOS Y SERVICIOS ─────────────
    // (antes "Gestión administrativa"). Orden: primero espacios e
    // infraestructura, luego las áreas/gestión y los demás servicios.
    // ---- Espacios e infraestructura ----
    {
        id: 'q34',
        chapter: 'servicios',
        type: 'single',
        tag: 'Evidencia · Planta física',
        context: 'Piensa en los espacios que tu hijo(a) usa habitualmente: salones, baños, zonas comunes, zonas deportivas, granja…',
        text: '¿Cómo describiría su estado general?',
        options: [
            'Excelente y uniforme en todos los espacios',
            'Bueno en general, con algún espacio puntual por mejorar',
            'Aceptable, con varios espacios por mejorar',
            'Malo en varios espacios, requiere atención pronta',
            'Deficiente',
            'No los conozco directamente',
        ],
    },
    {
        id: 'q34a',
        chapter: 'servicios',
        type: 'likert',
        tag: 'Satisfacción · Seguridad instalaciones',
        text: '¿Qué tan satisfecho(a) está con la seguridad de las instalaciones del colegio (salones, baños, zonas comunes, deportivas, granja)?',
        anchors: ['Nada satisfecho(a)', 'Totalmente satisfecho(a)'],
    },
    {
        id: 'q34am',
        chapter: 'servicios',
        type: 'open',
        tag: 'Abierta condicional · Seguridad instalaciones',
        condition: (a) => typeof a.q34a === 'number' && a.q34a <= 6,
        text: 'Indique qué aspectos de la seguridad de las instalaciones le preocupan.',
        placeholder: 'Cuéntanos un ejemplo concreto…',
    },
    {
        id: 'q34b',
        chapter: 'servicios',
        type: 'likert',
        tag: 'Satisfacción · Servicios generales',
        text: '¿Qué tan satisfecho(a) está con la limpieza y presentación de las zonas del colegio (limpieza, vigilancia, cuidado y conservación de los espacios)?',
        anchors: ['Nada satisfecho(a)', 'Totalmente satisfecho(a)'],
    },
    {
        id: 'q34c',
        chapter: 'servicios',
        type: 'open',
        tag: 'Abierta condicional · Servicios generales',
        condition: (a) => typeof a.q34b === 'number' && a.q34b <= 6,
        text: 'Indique qué aspectos se deben mejorar.',
        placeholder: 'Cuéntanos un ejemplo concreto…',
    },
    // ---- Áreas / gestión y servicios ----
    {
        id: 'q30',
        chapter: 'servicios',
        type: 'matrix',
        tag: 'Matriz · Áreas de apoyo',
        text: 'Para cada área o gestión, marque la opción que mejor describa su experiencia este semestre.',
        help: 'Si no tuvo contacto con un área, marque «Sin contacto este semestre».',
        columns: ['Excelente atención', 'Atención aceptable', 'Atención inadecuada', 'Sin contacto este semestre'],
        rows: [
            'Rectoría (Andrés Donato)',
            'Vicerrectoría de Convivencia y Bienestar Integral (Gimena Guavita)',
            'Vicerrectoría Académica',
            'Coordinación académica',
            'Coordinación de Convivencia y Bienestar Integral',
            'Departamento de Psicoorientación (psicólogas que atienden a sus hijos)',
            'Departamento Terapéutico (fonoaudiología, terapia ocupacional, fisioterapia, entre otros)',
            'Departamento de CDI académico',
            'Secretaría Académica (Nakarit Tarquino)',
            'Secretaría General (Ibetd Zullin Plazas)',
            'Enfermería',
            'Departamento Contable',
            'Soporte Técnico, Redes Sociales y página Web',
        ],
    },
    {
        id: 'q31',
        chapter: 'servicios',
        type: 'likert',
        tag: 'Satisfacción · Áreas de apoyo',
        context: 'Piensa en tu experiencia general con las áreas con las que tuviste contacto este semestre.',
        text: '¿Qué tan satisfecho(a) está con la calidad del servicio y la oportunidad de la respuesta?',
        anchors: ['Nada satisfecho(a)', 'Totalmente satisfecho(a)'],
    },
    {
        id: 'q31b',
        chapter: 'servicios',
        type: 'open',
        tag: 'Abierta condicional · Áreas de apoyo',
        condition: (a) => typeof a.q31 === 'number' && a.q31 <= 6,
        text: 'Indique con qué área(s) tuvo dificultades y por qué.',
        placeholder: 'Cuéntanos un ejemplo concreto…',
    },
    {
        id: 'q32a',
        chapter: 'servicios',
        type: 'single',
        tag: 'Filtro · Transporte',
        text: '¿Su familia usa el servicio de transporte escolar (ruta)?',
        options: ['Sí, lo usamos', 'No lo usamos'],
    },
    {
        id: 'q32b',
        chapter: 'servicios',
        type: 'likert',
        tag: 'Satisfacción · Transporte',
        condition: (a) => a.q32a === 'Sí, lo usamos',
        text: '¿Qué tan satisfecho(a) está con el servicio de transporte escolar este semestre?',
        anchors: ['Nada satisfecho(a)', 'Totalmente satisfecho(a)'],
    },
    {
        id: 'q32bm',
        chapter: 'servicios',
        type: 'open',
        tag: 'Abierta condicional · Transporte',
        condition: (a) => a.q32a === 'Sí, lo usamos' && typeof a.q32b === 'number' && a.q32b <= 6,
        text: 'Indique qué dificultades ha tenido con el servicio de transporte.',
        placeholder: 'Cuéntanos un ejemplo concreto…',
    },
    // Restaurante (q33a–q33c) y cafetería (q33d–q33f): servicios separados,
    // cada uno con su filtro → satisfacción (1–10) → caja abierta si ≤ 6.
    {
        id: 'q33a',
        chapter: 'servicios',
        type: 'single',
        tag: 'Filtro · Restaurante',
        text: '¿Su hijo(a) usa el servicio de restaurante?',
        options: ['Sí, lo usa', 'No lo usa'],
    },
    {
        id: 'q33b',
        chapter: 'servicios',
        type: 'likert',
        tag: 'Satisfacción · Restaurante',
        condition: (a) => a.q33a === 'Sí, lo usa',
        text: '¿Qué tan satisfecho(a) está con el servicio de restaurante este semestre?',
        anchors: ['Nada satisfecho(a)', 'Totalmente satisfecho(a)'],
    },
    {
        id: 'q33c',
        chapter: 'servicios',
        type: 'open',
        tag: 'Abierta condicional · Restaurante',
        condition: (a) => a.q33a === 'Sí, lo usa' && typeof a.q33b === 'number' && a.q33b <= 6,
        text: 'Indique con qué aspectos del restaurante tuvo dificultades y por qué.',
        placeholder: 'Cuéntanos un ejemplo concreto…',
    },
    {
        id: 'q33d',
        chapter: 'servicios',
        type: 'single',
        tag: 'Filtro · Cafetería',
        text: '¿Su hijo(a) usa el servicio de cafetería?',
        options: ['Sí, lo usa', 'No lo usa'],
    },
    {
        id: 'q33e',
        chapter: 'servicios',
        type: 'likert',
        tag: 'Satisfacción · Cafetería',
        condition: (a) => a.q33d === 'Sí, lo usa',
        text: '¿Qué tan satisfecho(a) está con el servicio de cafetería este semestre?',
        anchors: ['Nada satisfecho(a)', 'Totalmente satisfecho(a)'],
    },
    {
        id: 'q33f',
        chapter: 'servicios',
        type: 'open',
        tag: 'Abierta condicional · Cafetería',
        condition: (a) => a.q33d === 'Sí, lo usa' && typeof a.q33e === 'number' && a.q33e <= 6,
        text: 'Indique con qué aspectos de la cafetería tuvo dificultades y por qué.',
        placeholder: 'Cuéntanos un ejemplo concreto…',
    },
    // ───────────── V. GESTIÓN DE LA COMUNIDAD ─────────────
    {
        id: 'q35',
        chapter: 'comunidad',
        type: 'single',
        tag: 'Convivencia leve',
        text: 'En este semestre, ¿cuántas veces le ha mencionado su hijo(a) que se sintió incómodo(a) o con disgusto en alguna situación del colegio?',
        options: ['Ninguna', 'Una', 'Entre 2 y 3', 'Más de 3 veces', 'Mi hijo(a) no me ha contado sobre este tipo de situaciones'],
    },
    {
        id: 'q36',
        chapter: 'comunidad',
        type: 'single',
        tag: 'Convivencia grave',
        text: 'En este semestre, ¿le ha mencionado su hijo(a) alguna situación en la que se sintió irrespetado(a) o inseguro(a) en el colegio?',
        options: ['No, ninguna situación de ese tipo', 'Sí, una situación', 'Sí, más de una situación', 'Mi hijo(a) no me cuenta este tipo de situaciones'],
    },
    {
        id: 'q37',
        chapter: 'comunidad',
        type: 'single',
        tag: 'Percepción de cambio · Clima',
        context: 'Comparado con el año pasado…',
        text: '¿Cómo describiría hoy la motivación con que su hijo(a) se despierta para ir al colegio?',
        options: CAMBIO,
    },
    {
        id: 'q38',
        chapter: 'comunidad',
        type: 'likert',
        tag: 'Satisfacción · Clima escolar',
        text: '¿Qué tan satisfecho(a) está con el clima escolar y la gestión de la convivencia en el GBCMC?',
        anchors: ['Nada satisfecho(a)', 'Totalmente satisfecho(a)'],
    },
    {
        id: 'q38m',
        chapter: 'comunidad',
        type: 'open',
        tag: 'Abierta condicional · Clima escolar',
        condition: (a) => typeof a.q38 === 'number' && a.q38 <= 6,
        text: 'Indique qué situaciones del clima escolar o la convivencia deberían mejorar.',
        placeholder: 'Cuéntanos un ejemplo concreto…',
    },
    {
        id: 'q39',
        chapter: 'comunidad',
        type: 'likert',
        tag: 'Satisfacción · Acogida nuevos',
        condition: (a) => a.q6 === 'Sí, es estudiante nuevo',
        context: 'Como tu hijo(a) ingresó este año, queremos saber cómo los recibimos.',
        text: '¿Qué tan satisfecho(a) está con el proceso de acogida e integración de su familia y de su hijo(a) durante este primer semestre?',
        anchors: ['Nada satisfecho(a)', 'Totalmente satisfecho(a)'],
    },
    {
        id: 'q39m',
        chapter: 'comunidad',
        type: 'open',
        tag: 'Abierta condicional · Acogida nuevos',
        condition: (a) => a.q6 === 'Sí, es estudiante nuevo' && typeof a.q39 === 'number' && a.q39 <= 6,
        text: 'Indique qué pudo mejorar en el proceso de acogida e integración de su familia.',
        placeholder: 'Cuéntanos un ejemplo concreto…',
    },
    {
        id: 'q40',
        chapter: 'comunidad',
        type: 'single',
        tag: 'Filtro · Inclusión (Decreto 1421/2017)',
        text: '¿Su hijo(a) tiene alguna habilidad diversa que requiera apoyos pedagógicos, terapias o ajustes razonables (PIAR)?',
        options: ['Sí', 'No', 'Prefiero no responder'],
    },
    {
        id: 'q41',
        chapter: 'comunidad',
        type: 'single',
        tag: 'Evidencia · Inclusión',
        condition: (a) => a.q40 === 'Sí',
        text: 'En este semestre, ¿el colegio implementó los apoyos pedagógicos o ajustes razonables acordados en el PIAR?',
        options: ['Sí, de forma completa y oportuna', 'Sí, pero de forma parcial o tardía', 'No los implementó como se acordó', 'No estoy seguro(a)'],
    },
    {
        id: 'q42',
        chapter: 'comunidad',
        type: 'single',
        tag: 'Percepción de cambio · Inclusión',
        condition: (a) => a.q40 === 'Sí',
        context: 'Comparado con el año pasado…',
        text: '¿Cómo describiría hoy el progreso de su hijo(a) gracias a estos apoyos?',
        options: CAMBIO,
    },
    {
        id: 'q43',
        chapter: 'comunidad',
        type: 'likert',
        tag: 'Satisfacción · Inclusión',
        condition: (a) => a.q40 === 'Sí',
        text: '¿Qué tan satisfecho(a) está con los apoyos pedagógicos y los ajustes razonables que el colegio brinda a su hijo(a)?',
        anchors: ['Nada satisfecho(a)', 'Totalmente satisfecho(a)'],
    },
    {
        id: 'q43m',
        chapter: 'comunidad',
        type: 'open',
        tag: 'Abierta condicional · Inclusión',
        condition: (a) => a.q40 === 'Sí' && typeof a.q43 === 'number' && a.q43 <= 6,
        text: 'Indique qué aspectos de los apoyos pedagógicos y los ajustes razonables deberían mejorar.',
        placeholder: 'Cuéntanos un ejemplo concreto…',
    },
    // ───────────── VI. CIERRE ─────────────
    {
        id: 'q44',
        chapter: 'cierre',
        type: 'likert',
        tag: 'Satisfacción global',
        text: 'En general, considerando todos los aspectos, ¿qué tan satisfecho(a) está con su experiencia este semestre en el GBCMC?',
        anchors: ['Nada satisfecho(a)', 'Totalmente satisfecho(a)'],
    },
    {
        id: 'q44b',
        chapter: 'cierre',
        type: 'open',
        tag: 'Razón satisfacción global',
        text: '¿Cuál es la principal razón de la calificación que acaba de dar?',
        placeholder: 'Cuéntanos en tus palabras…',
    },
    {
        id: 'q45',
        chapter: 'cierre',
        type: 'likert',
        tag: 'NPS',
        text: 'Considerando su experiencia durante este semestre, ¿qué tan probable es que recomiende el GBCMC a otras familias?',
        anchors: ['Nada probable', 'Totalmente probable'],
    },
    {
        id: 'q46',
        chapter: 'cierre',
        type: 'open',
        tag: 'Razón NPS',
        text: '¿Cuál es la principal razón de la calificación que acaba de dar?',
        placeholder: 'Cuéntanos en tus palabras…',
    },
    {
        id: 'q47',
        chapter: 'cierre',
        type: 'open',
        tag: 'Fortalezas',
        text: '¿Cuáles considera que son las principales fortalezas del colegio este semestre?',
        placeholder: 'Lo que el colegio debería seguir haciendo…',
    },
    {
        id: 'q48',
        chapter: 'cierre',
        type: 'open',
        tag: 'Prioridad de mejora',
        text: '¿Qué debería mejorar prioritariamente el colegio para fortalecer la experiencia de las familias y los estudiantes?',
        placeholder: 'Lo más importante primero…',
    },
];
/** Versión del instrumento, para versionado de respuestas. */
export const INSTRUMENT_VERSION = 'v2-familias';
//# sourceMappingURL=data.js.map